import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { exhaustMap, map, take } from 'rxjs';
import {
  AuthService,
  isEqualValidator,
  isHttpErrorResponse,
  ServerConfigData,
  ServerConfigService,
} from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../../../../components/dialog/dialog.service';

@Component({
  selector: 'app-reset-password-form',
  imports: [MatButton, MatError, MatFormField, MatInput, ReactiveFormsModule],
  templateUrl: './reset-password-form.component.html',
  styleUrl: './reset-password-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordFormComponent {
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _route = inject(ActivatedRoute);
  private readonly _auth = inject(AuthService);
  private readonly _dialog = inject(DialogService);
  private readonly _router = inject(Router);
  private readonly _snackbar = inject(MatSnackBar);
  protected readonly serverConfig =
    inject<ServerConfigService<ServerConfigData>>(ServerConfigService);

  protected readonly form = this._fb.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(this.serverConfig.data.userPasswordValidation.minlength),
          Validators.maxLength(this.serverConfig.data.userPasswordValidation.maxlength),
        ],
      ],
      verifyPassword: ['', [Validators.required]],
    },
    {
      validators: [isEqualValidator('password', 'verifyPassword')],
    }
  );

  get controls() {
    return this.form.controls;
  }

  protected submit(): void {
    this._route.paramMap
      .pipe(
        take(1),
        map((pm) => {
          const token = pm.get('resetToken');
          if (!token) {
            throw new Error('RESET_TOKEN_UNDEFINED');
          }
          return token;
        }),
        exhaustMap((resetToken) =>
          this._auth.passwordChange({
            resetToken,
            newPassword: this.controls.password.value,
            verifyNewPassword: this.controls.verifyPassword.value,
          })
        )
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/login']);
          this._snackbar.open(
            'Wachtwoord is succesvol aangepast. Je kunt nu inloggen.',
            'OK'
          );
        },
        error: (e) => {
          const dialog = {
            title: 'Foutmelding',
            message:
              'Er gaat iets fout met het aanpassen van je wachtwoord. Probeer het later nog eens.',
            confirmText: 'Begrepen',
          };

          if (e instanceof Error && e.message === 'RESET_TOKEN_UNDEFINED') {
            dialog.title = 'Reset token niet gevonden';
            dialog.message =
              'Er is geen reset token opgegeven. Zonder token kan er geen reset worden gedaan.';
          }

          if (isHttpErrorResponse(e)) {
            switch (e.error.detail.errorCode) {
              case 'PASS_RESET_TOKEN_EXPIRED':
                dialog.title = 'Reset verlopen';
                dialog.message =
                  'Je hebt te lang gewacht met het veranderen van je wachtwoord. Probeer opnieuw in te loggen om zo opnieuw je wachtwoord te kunnen aanpassen.';

                break;
              case 'USER_PASSWORD_CHANGE_PASS_VALUE_NOT_CHANGED':
                dialog.title = 'Wachtwoord niet aangepast';
                dialog.message =
                  'Het opgegeven wachtwoord is nog steeds het zelfde als het oude. Probeer een nieuw wachtwoord te verzinnen om de veiligheid beter te kunnen garanderen.';

                break;
            }
          }

          this._dialog.alert(dialog);
        },
      });
  }
}
